<template>
  <div class="document-section mt-4">
    <!-- Título de la sección -->
    <h2 v-if="title" class="mb-2 px-6 font-rubik font-weight-bold" style="font-size: 1.6rem; color: black">{{ title }}</h2>

    <!-- Mensaje cuando no hay documentos en esta sección -->
    <div v-if="!isLoading && allDocumentsInSection.length === 0 && !noDocumentsMessage">
      <div v-if="showUploader" class="mb-6">
        <FileUploader :subjectId="subjectId" @document-uploaded="onDocumentUploaded" @back="showUploader = false" />
      </div>
      <v-card v-else class="mb-6 rounded-0 px-0 rounded-lg" flat color="transparent"
        width="fit-content">
        <v-card-text class="py-2 px-0 d-flex px-6">
          <div class="text-start d-flex flex-column align-start font-rubik">
            <span style="font-size: 18px;" class="font-weight-medium py-4">{{ emptyMessage }}</span>
            <span style="font-size: 14px;" class="grey--text mb-4">{{ emptyDescription }}</span>
        
          </div>

          <v-btn
          v-if="false"
          color="primary" @click="showUploader = true" class="mt-2 ml-10  primary white--text" outlined style="padding-left: 16px !important;">
            <lucide-upload color="white" :size="16" class="mr-2"></lucide-upload>

              <span style="color: white !important;">Subir apuntes</span>
            </v-btn>
        </v-card-text>
      </v-card>
    </div>

    <!-- Mensaje personalizado cuando no hay documentos -->
    <v-card v-else-if="!isLoading && allDocumentsInSection.length === 0 && noDocumentsMessage"
      class="mb-6 rounded-0 mx-auto py-3 px-5 rounded-lg" flat color="rgb(235 235 235 / 58%)" width="fit-content">
      <v-card-text class="py-2 px-0">
        <div class="text-center d-flex flex-column align-center font-rubik">
          <span style="font-size: 20px;" class="font-weight-medium py-4">{{ noDocumentsMessage }}</span>
          <span v-if="noDocumentsDescription" style="font-size: 14px;" class="grey--text">{{ noDocumentsDescription
            }}</span>
        </div>
      </v-card-text>
    </v-card>

    <!-- Mensaje cuando hay documentos pero el filtro no devuelve resultados -->
    <v-card v-else-if="!isLoading && allDocumentsInSection.length > 0 && documents.length === 0 && searchQuery"
      class="mb-6 rounded-0 mx-auto py-3 px-5 rounded-lg" flat color="rgb(235 235 235 / 58%)" width="fit-content">
      <v-card-text class="py-2 px-0">
        <div class="text-center d-flex flex-column align-center font-rubik">
          <span style="font-size: 16px;" class="font-weight-medium py-4">No hay documentos que coincidan con "{{
            searchQuery }}"</span>
        </div>
      </v-card-text>
    </v-card>

    <!-- Contenedor de documentos -->
    <v-card v-if="documents.length > 0" class="document-table-container rounded-xl"
      :class="viewMode === 'list' ? 'pa-3' : ''" flat :color="viewMode === 'list' ? 'white' : 'transparent'">

      <!-- Vista de lista -->
      <v-data-table v-if="viewMode === 'list'" :headers="headers" :items="documents" :items-per-page="-1"
        hide-default-footer class="elevation-0 document-table" @click:row="onViewDocument"
        :item-class="() => 'clickable-row'">

        <!-- Ícono del documento -->
        <template v-slot:item.icon="{ item }">
          <div style="position: relative;">
            <!-- Loading overlay para vista de lista -->
            <div v-if="loadingDocumentId === item.id" 
                 style="position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: rgba(255,255,255,0.8); display: flex; align-items: center; justify-content: center; z-index: 10;">
              <v-progress-circular indeterminate color="primary" size="16"></v-progress-circular>
            </div>
            <svg xmlns="http://www.w3.org/2000/svg" version="1.1" xmlns:xlink="http://www.w3.org/1999/xlink" width="24"
              height="24" x="0" y="0" viewBox="0 0 512 512" style="enable-background:new 0 0 512 512; margin-top: 4px;"
              xml:space="preserve">
              <g>
                <path
                  d="M104 0C86.328 0 72 14.328 72 32v448c0 17.672 14.328 32 32 32h304c17.672 0 32-14.328 32-32V128L312 0H104z"
                  style="" fill="#e2e7f0" data-original="#e2e7f0" class=""></path>
                <path
                  d="M344 128h96L312 0v96c0 17.672 14.328 32 32 32zM192 160h128c4.416 0 8 3.584 8 8s-3.584 8-8 8H192c-4.416 0-8-3.584-8-8s3.584-8 8-8zM144 328h224c4.416 0 8 3.584 8 8s-3.584 8-8 8H144c-4.416 0-8-3.584-8-8s3.584-8 8-8zM144 424h128c4.416 0 8 3.584 8 8s-3.584 8-8 8H144c-4.416 0-8-3.584-8-8s3.584-8 8-8zM144 376h224c4.416 0 8 3.584 8 8s-3.584 8-8 8H144c-4.416 0-8-3.584-8-8s3.584-8 8-8zM144 280h224c4.416 0 8 3.584 8 8s-3.584 8-8 8H144c-4.416 0-8-3.584-8-8s3.584-8 8-8zM144 232h224c4.416 0 8 3.584 8 8s-3.584 8-8 8H144c-4.416 0-8-3.584-8-8s3.584-8 8-8z"
                  style="" fill="#9dacba" data-original="#9dacba" class="pt-2"></path>
                <path
                  d="m344 128 75.28 75.28a16.019 16.019 0 0 1 4.72 11.36V480c0 8.84-7.16 16-16 16H120c-8.84 0-16 7.16-16 16h304c17.672 0 32-14.328 32-32V128h-96z"
                  style="" fill="#c9d5e3" data-original="#c9d5e3"></path>
              </g>
            </svg>
          </div>
        </template>

        <!-- Título y descripción -->
        <template v-slot:item.title="{ item }">
          <div>
            <div style="font-size: 15px;" class="font-rubik font-weight-medium">{{ item.name }}</div>
            <div class="text-caption text--secondary" style="font-size: 12px;">{{ item.description }}</div>
            
            <!-- Barra de progreso en vista de lista -->
            <div v-if="item.progress_percentage !== undefined && item.total_items > 0" class="mt-2 mb-2">
              <div class="d-flex align-items-center justify-space-between mb-1">
                <span class="font-rubik" style="font-size: 0.7rem; color: #666;">Progreso</span>
                <span class="font-rubik font-weight-medium" style="font-size: 0.7rem; color: #333;">{{ Math.round(item.progress_percentage) }}%</span>
              </div>
              <v-progress-linear 
                :value="item.progress_percentage" 
                color="primary"
                height="6" 
                background-color="#e2e2e2"
                class="rounded-lg">
              </v-progress-linear>
            </div>
            
            <!-- Chips de contenido educativo en vista de lista -->
            <div v-if="item.flashcard_count > 0 || item.total_questions > 0 || item.explanation_count > 0" 
                 class="d-flex flex-wrap gap-1 mt-2">
              <!-- Chip de flashcards -->
              <div v-if="item.flashcard_count > 0" 
                   class="content-chip-small flashcard-chip">
                <div class="chip-icon-small">
                  <v-icon size="14" color="#2d7738">mdi-cards-outline</v-icon>
                </div>
                <span class="chip-text-small">{{ item.flashcard_count }}</span>
              </div>
              
              <!-- Chip de preguntas de quiz -->
              <div v-if="item.total_questions > 0" 
                   class="content-chip-small quiz-chip">
                <div class="chip-icon-small">
                  <v-icon size="14" color="#e65100">mdi-help-circle-outline</v-icon>
                </div>
                <span class="chip-text-small">{{ item.total_questions }}</span>
              </div>
              
              <!-- Chip de explicaciones -->
              <div v-if="item.explanation_count > 0" 
                   class="content-chip-small explanation-chip">
                <div class="chip-icon-small">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" 
                       stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                    <line x1="12" x2="12" y1="19" y2="22"/>
                    <line x1="8" x2="16" y1="22" y2="22"/>
                  </svg>
                </div>
                <span class="chip-text-small">{{ item.explanation_count }}</span>
              </div>
            </div>
          </div>
        </template>

        <!-- Autor (solo si se muestra) -->
        <template v-if="showAuthor" v-slot:item.author="{ item }">
          <span class="font-rubik">{{ item.author }}</span>
        </template>

        <!-- Número de páginas (solo si no es null o 0) -->
        <template v-slot:item.num_pages="{ item }">
          <span v-if="item.num_pages && item.num_pages > 0" class="font-rubik">{{ item.num_pages }}</span>
          <span v-else class="font-rubik text--disabled">-</span>
        </template>

        <!-- Fecha de creación -->
        <template v-slot:item.created_at="{ item }">
          {{ formatDate(item.created_at) }}
        </template>

        <!-- Acciones -->
        <template v-slot:item.actions="{ item }">
          <document-actions-menu v-if="canDelete && canRename" :document="item" @view="onViewDocument"
            @rename="canRename ? onRenameDocument : undefined" @share="onShareDocument" @copy="onCopyDocument"
            @delete="canDelete ? onDeleteDocument : undefined" @toggle-privacy="onTogglePrivacy" :can-delete="canDelete" :can-rename="canRename" />
        </template>
      </v-data-table>

      <!-- Vista de cuadrícula -->
      <v-row v-else class="px-6 py-2" style="padding-bottom: 60px !important;">
        <v-col v-for="document in documents" :key="document.id" cols="12" md="6" xl="4" class="pa-1">
          <v-card class="d-flex pb-0 document-card shadow-sm" flat hover 
          :ripple="false" width="100%"
          :class="$vuetify.breakpoint.xs ? 'flex-column' : ''"
            style="position: relative; user-select: none;"
            :style="$vuetify.breakpoint.xs ? 'height: auto' : 'height: 280px;'"
            @click="onViewDocument(document)">

            <!-- Loading overlay para vista de cuadrícula -->
            <div v-if="loadingDocumentId === document.id" 
                 style="position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: rgba(255,255,255,0.9); display: flex; align-items: center; justify-content: center; z-index: 20; border-radius: 16px;">
              <div class="d-flex flex-column align-center">
                <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
                <span class="font-rubik mt-2" style="font-size: 0.9rem; color: #666;">Cargando...</span>
              </div>
            </div>

            <!-- Badge "Más popular" -->
            <div v-if="mostPopularId && document.id === mostPopularId" 
                 class="popular-badge">
              <span class="popular-badge-text">Más popular</span>
            </div>

            <!-- Badge "Más estudiado" -->
            <div v-if="mostStudiedId && document.id === mostStudiedId && document.id !== mostPopularId"
                 class="studied-badge">
              <span class="studied-badge-text">Más estudiado</span>
            </div>

            <div style="position: absolute; top: 10px; right: 10px;">
              <document-actions-menu v-if="canDelete && canRename" :size="20" :document="document"
                @view="onViewDocument(document)" @rename="canRename ? onRenameDocument(document) : undefined"
                @share="onShareDocument(document)" @copy="onCopyDocument(document)"
                @delete="canDelete ? onDeleteDocument(document) : undefined" @toggle-privacy="onTogglePrivacy" :can-delete="canDelete"
                :can-rename="canRename" style="position: absolute; top: 0; right: 0;" />
            </div>

            <div class="d-flex pl-0 "
          :class="$vuetify.breakpoint.xs ? 'flex-column justify-center align-center' : 'justify-start'"
            style="flex: 1 1 auto;">
              <!-- Previsualización o ícono -->
              <div class="document-preview d-flex align-center justify-center"
                style="min-width: 120px; height: 100%; min-height: 100px; border-right: 1px solid rgb(247 247 247);"
                :class="$vuetify.breakpoint.xs ? 'mb-3' : ''"
                :style="$vuetify.breakpoint.xs ? 'width: 100%;' : 'width: 180px; min-width: 180px;'"
                >
                <div v-if="document.preview_image" class="d-flex flex-column justify-center align-center"
                  style="width: 100%; height: 100%;">
                  <v-img :src="document.preview_image" 
                  :contain="false" 
                  class=""
                  :style="$vuetify.breakpoint.xs ? 'max-height: 200px; border-bottom: 1px solid rgb(247 247 247);' : ''"
                    style="max-width: 100%; max-height: 100%;object-fit: cover; background-color: white;" />
                </div>
                <template v-else>
                  <svg v-if="document.mimeType === 'application/pdf' || document.mimeType === 'text/plain'"
                    xmlns="http://www.w3.org/2000/svg" width="50" height="70" viewBox="0 0 512 512" class="mx-auto my-auto">
                    <g>
                      <path
                        d="M104 0C86.328 0 72 14.328 72 32v448c0 17.672 14.328 32 32 32h304c17.672 0 32-14.328 32-32V128L312 0H104z"
                        fill="#e2e7f0" />
                      <path d="M344 128h96L312 0v96c0 17.672 14.328 32 32 32z" fill="#c9d5e3" />
                      <path
                        d="M192 160h128c4.416 0 8 3.584 8 8s-3.584 8-8 8H192c-4.416 0-8-3.584-8-8s3.584-8 8-8zM144 328h224c4.416 0 8 3.584 8 8s-3.584 8-8 8H144c-4.416 0-8-3.584-8-8s3.584-8 8-8zM144 424h128c4.416 0 8 3.584 8 8s-3.584 8-8 8H144c-4.416 0-8-3.584-8-8s3.584-8 8-8zM144 376h224c4.416 0 8 3.584 8 8s-3.584 8-8 8H144c-4.416 0-8-3.584-8-8s3.584-8 8-8zM144 280h224c4.416 0 8 3.584 8 8s-3.584 8-8 8H144c-4.416 0-8-3.584-8-8s3.584-8 8-8zM144 232h224c4.416 0 8 3.584 8 8s-3.584 8-8 8H144c-4.416 0-8-3.584-8-8s3.584-8 8-8z"
                        fill="#9dacba" />
                    </g>
                  </svg>
                  <slot v-else-if="document.mimeType === 'image/collection'" name="image-collection" :width="50"
                    :height="70"></slot>
                  <slot
                    v-else-if="document.mimeType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation'"
                    name="presentation" :width="50" :height="70"></slot>
                  <svg v-else xmlns="http://www.w3.org/2000/svg" width="50" height="70" viewBox="0 0 24 24" fill="none"
                    stroke="#b0bec5" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="12" y1="18" x2="12" y2="12"></line>
                    <line x1="9" y1="15" x2="15" y2="15"></line>
                  </svg>
                </template>
              </div>

              <!-- Contenido del documento -->
              <div class="d-flex flex-column pa-3" style="flex-grow: 1; overflow: hidden;">

                <!-- Título y menú de acciones -->
                <div class="d-flex align-center pb-1 pr-6">
                  <div style="flex-grow: 1; overflow: hidden; margin-right: 8px;">
                    <h3 class="font-weight-bold mb-1 font-rubik"
                      style="font-size: 1.1rem !important; line-height: 1.2; color: #0E121B; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; text-overflow: ellipsis;">
                      {{ document.name }}
                    </h3>
                  </div>
                </div>

                <!-- Descripción -->
                <p v-if="document.description" class="font-rubik text--secondary mb-2"
                  style="font-size: 0.85rem !important; line-height: 1.3; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; text-overflow: ellipsis; max-height: 3.9em;">
                  {{ document.description }}
                </p>

                <div class="d-flex align-center flex-wrap mt-2 mb-1" style="gap: 8px;">

                    <!-- Número de páginas (solo si no es null o 0) -->
                    <div v-if="document.num_pages && document.num_pages > 0" class="d-flex align-center">
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none"
                        stroke="rgb(105 105 105)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        class="mr-1">
                        <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>
                        <polyline points="14 2 14 8 20 8"/>
                        <line x1="16" y1="13" x2="8" y2="13"/>
                        <line x1="16" y1="17" x2="8" y2="17"/>
                        <line x1="10" y1="9" x2="8" y2="9"/>
                      </svg>
                      <span class="font-rubik"
                        style="color: rgb(105 105 105) !important; font-size: 0.7rem;">{{ document.num_pages }} pág.</span>
                    </div>
                    
                    <!-- Autor (solo si se debe mostrar) -->
                    <div v-if="showAuthor" class="d-flex align-center">
                      <svg style="width: 12px; height: 12px;"
                        xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                        stroke="rgb(105 105 105)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        class="mr-1">
                        <path d="M18 20a6 6 0 0 0-12 0" />
                        <circle cx="12" cy="10" r="4" />
                        <circle cx="12" cy="12" r="10" />
                      </svg>
                      <span class="font-rubik"
                        style="color: rgb(105 105 105) !important; font-size: 0.7rem; max-width: 80px; text-overflow: ellipsis; overflow: hidden; white-space: nowrap;">{{
                          document.author || 'Anónimo' }}</span>
                    </div>

                    <!-- Fecha de creación -->
                    <div class="d-flex align-center">
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none"
                        stroke="rgb(105 105 105)" stroke-width="2"
                        stroke-linecap="round" stroke-linejoin="round" class="mr-1">
                        <line x1="10" x2="14" y1="2" y2="2" />
                        <line x1="12" x2="15" y1="14" y2="11" />
                        <circle cx="12" cy="14" r="8" />
                      </svg>
                      <span class="font-rubik" style="color: rgb(105 105 105) !important; font-size: 0.7rem;">{{
                        formatDate(document.created_at) }}</span>
                    </div>
                  </div>

                <!-- Metadatos (autor y fecha) -->
                <div class="d-flex flex-column mt-auto pb-0">
                  
                  <!-- Barra de progreso -->
                  <div v-if="document.progress_percentage !== undefined && document.progress_percentage > 0 && document.total_items > 0" class="mb-2">
                    <div class="d-flex align-items-center justify-space-between mb-1">
                      <span class="font-rubik" style="font-size: 0.7rem; color: #666;">Progreso</span>
                      <span class="font-rubik font-weight-medium" style="font-size: 0.7rem; color: #333;">{{ Math.round(document.progress_percentage) }}%</span>
                    </div>
                    <v-progress-linear 
                      :value="document.progress_percentage" 
                      color="primary"
                      height="6" 
                      background-color="#e2e2e2"
                      class="rounded-lg">
                    </v-progress-linear>
                  </div>
                  
                  <!-- Chips de contenido educativo -->
                  <div v-if="document.flashcard_count > 0 || document.total_questions > 0 || document.explanation_count > 0" 
                       class="d-flex flex-wrap gap-1">
                    <!-- Chip de flashcards -->
                    <div v-if="document.flashcard_count > 0" 
                         class="content-chip-compact flashcard-chip">
                      <div class="chip-icon-compact">
                        <v-icon size="12" color="#2d7738">mdi-cards-outline</v-icon>
                      </div>
                      <span class="chip-text-compact">{{ document.flashcard_count }}</span>
                      <span class="chip-label-compact">flashcards</span>
                    </div>
                    
                    <!-- Chip de preguntas de quiz -->
                    <div v-if="document.total_questions > 0" 
                         class="content-chip-compact quiz-chip">
                      <div class="chip-icon-compact">
                        <v-icon size="12" color="#e65100">mdi-help-circle-outline</v-icon>
                      </div>
                      <span class="chip-text-compact">{{ document.total_questions }}</span>
                      <span class="chip-label-compact">preguntas</span>
                    </div>
                    
                    <!-- Chip de explicaciones -->
                    <div v-if="document.explanation_count > 0" 
                         class="content-chip-compact explanation-chip">
                      <div class="chip-icon-compact">
                        <v-icon size="12" color="#1565c0">mdi-account-voice</v-icon>
                      </div>
                      <span class="chip-text-compact">{{ document.explanation_count }}</span>
                      <span class="chip-label-compact">audios</span>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-card>
  </div>
</template>

<script>
import DocumentActionsMenu from './DocumentActionsMenu.vue';
import FileUploader from './upload/components/FileUploader.vue';
import { Upload } from 'lucide-vue';

export default {
  name: 'DocumentDisplaySection',
  components: {
    DocumentActionsMenu,
    FileUploader,
    LucideUpload: Upload
  },
  props: {
    // Título de la sección (ej. "Mis Documentos")
    title: {
      type: String,
      default: ''
    },
    // Documentos filtrados a mostrar
    documents: {
      type: Array,
      required: true
    },
    // Todos los documentos en esta sección (para mensajes de error)
    allDocumentsInSection: {
      type: Array,
      default: () => []
    },
    // Modo de visualización ('grid' o 'list')
    viewMode: {
      type: String,
      default: 'grid',
      validator: value => ['grid', 'list'].includes(value)
    },
    // Consulta de búsqueda (para mensajes cuando no hay coincidencias)
    searchQuery: {
      type: String,
      default: ''
    },
    // Si se está cargando datos
    isLoading: {
      type: Boolean,
      default: false
    },
    // Cabeceras para la tabla
    headers: {
      type: Array,
      default: () => []
    },
    // Si se debe mostrar el autor
    showAuthor: {
      type: Boolean,
      default: false
    },
    // Si el usuario puede eliminar documentos
    canDelete: {
      type: Boolean,
      default: true
    },
    // Si el usuario puede renombrar documentos
    canRename: {
      type: Boolean,
      default: true
    },
    // Mensaje cuando no hay documentos
    emptyMessage: {
      type: String,
      default: 'No hay documentos disponibles'
    },
    // Descripción cuando no hay documentos
    emptyDescription: {
      type: String,
      default: 'Los documentos se mostrarán aquí cuando estén disponibles.'
    },
    // Mensaje personalizado cuando no hay documentos
    noDocumentsMessage: {
      type: String,
      default: ''
    },
    // Descripción personalizada cuando no hay documentos
    noDocumentsDescription: {
      type: String,
      default: ''
    },
    // ID de la asignatura (si aplica)
    subjectId: {
      type: [Number, String],
      default: null
    },
    // Prop para resetear el loading desde el componente padre
    resetLoading: {
      type: Boolean,
      default: false
    },
    // ID del documento más popular (para mostrar badge)
    mostPopularId: {
      type: [Number, String],
      default: null
    },
    // ID del documento más estudiado (para mostrar badge)
    mostStudiedId: {
      type: [Number, String],
      default: null
    }
  },
  data() {
    return {
      showUploader: false,
      loadingDocumentId: null
    };
  },
  watch: {
    // Watcher para resetear el loading cuando el padre lo indique
    resetLoading(newVal) {
      if (newVal) {
        this.loadingDocumentId = null;
        this.$emit('loading-reset');
      }
    }
  },
  methods: {
    // Formatear la fecha para mostrarla
    formatDate(dateString) {
      if (!dateString) return '';

      const date = new Date(dateString);
      date.setHours(date.getHours() - 2);

      if (isNaN(date.getTime())) return '';

      const now = new Date();
      const diff = now - date;

      const seconds = Math.floor(diff / 1000);
      const minutes = Math.floor(seconds / 60);
      const hours = Math.floor(minutes / 60);
      const days = Math.floor(hours / 24);
      const weeks = Math.floor(days / 7);
      const months = Math.floor(days / 30.44);
      const years = Math.floor(days / 365.25);

      if (seconds < 60) {
        return `hace ${seconds} s`;
      } else if (minutes < 60) {
        return `hace ${minutes} min`;
      } else if (hours < 24) {
        return `hace ${hours} h`;
      } else if (days < 7) {
        return `hace ${days} d`;
      } else if (weeks < 4.345) {
        return `hace ${weeks} sem`;
      } else if (months < 12) {
        return `hace ${months} m`;
      } else {
        return `hace ${years} a`;
      }
    },

    // Eventos que se emiten hacia el componente padre
    onViewDocument(document) {
      this.loadingDocumentId = document.id; // Activar loading para este documento
      this.$emit('view', document);
    },
    onRenameDocument(document) {
      this.$emit('rename', document);
    },
    onShareDocument(document) {
      this.$emit('share', document);
    },
    onCopyDocument(document) {
      this.$emit('copy', document);
    },
    onDeleteDocument(document) {
      this.$emit('delete', document);
    },
    onTogglePrivacy(document) {
      this.$emit('toggle-privacy', document);
    },
    // Método para manejar documentos subidos
    onDocumentUploaded(documentData) {
      this.$emit('document-uploaded', documentData);
      this.showUploader = false;
    }
  }
}
</script>

<style scoped>
.document-section {
  margin-bottom: 20px;
  min-height: 170px;
}

/* Estilos para el botón principal */
.v-btn.primary {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 800 !important;
  font-size: 0.9rem !important;
  border-radius: 13px !important;
  transition: filter 0.2s ease, transform 0.1s ease !important;
  height: 38px !important;
  padding-left: 24px !important;
  padding-right: 24px !important;
  border: 2px solid #4dd453 !important;
  background-color: #4dd453 !important;
  box-shadow: 0 4px 0 #42b848 !important;
  color: white;
}

.document-card {
  transition: border-top 0.3s ease;
  border-radius: 12px !important;
  height: 280px;
  overflow: hidden;
  border: 1px solid #ececec;
  position: relative; /* Required for ribbon positioning */
}

.document-card:hover {
}

.document-preview {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Estilos para los chips de contenido educativo - Vista de cuadrícula */
.content-chip {
  display: flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 6px;
  font-family: 'Rubik', -apple-system, system-ui, sans-serif;
  font-size: 0.6rem;
  font-weight: 500;
  transition: all 0.2s ease;
  cursor: default;
  backdrop-filter: blur(10px);
}


.chip-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 6px;
}

.chip-text {
  font-weight: 600;
  margin-right: 4px;
}

.chip-label {
  font-weight: 400;
  opacity: 0.8;
}

/* Chip de flashcards - Verde */
.flashcard-chip {
  background: #e8f8f5;
  color: #2d7738;
}

/* Chip de preguntas - Naranja */
.quiz-chip {
  background: #fff2df;
  color: #e65100;
}

/* Chip de explicaciones - Azul */
.explanation-chip {
  background: #DFF0FC;
  color: #1565c0;
}


/* Estilos para los chips de contenido educativo - Vista de lista (más pequeños) */
.content-chip-small {
  display: flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 8px;
  font-family: 'Rubik', -apple-system, system-ui, sans-serif;
  font-size: 0.7rem;
  font-weight: 500;
  transition: all 0.2s ease;
  cursor: default;
  margin-right: 4px;
  margin-bottom: 2px;
}



.chip-icon-small {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 4px;
}

.chip-text-small {
  font-weight: 600;
}

/* Aplicar los mismos colores a las versiones pequeñas */
.content-chip-small.flashcard-chip {
  background: linear-gradient(135deg, #e8f8f5 0%, #d1f7c4 100%);
  color: #2d7738;
  border: 1px solid rgba(45, 119, 56, 0.15);
}

.content-chip-small.quiz-chip {
  background: linear-gradient(135deg, #fff4e6 0%, #ffe0b3 100%);
  color: #e65100;
  border: 1px solid rgba(230, 81, 0, 0.15);
}

.content-chip-small.explanation-chip {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  color: #1565c0;
  border: 1px solid rgba(21, 101, 192, 0.15);
}

/* Estilos para chips compactos en vista de cuadrícula */
.content-chip-compact {
  display: flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: 4px;
  font-family: 'Rubik', -apple-system, system-ui, sans-serif;
  font-size: 0.55rem;
  font-weight: 500;
  transition: all 0.2s ease;
  cursor: default;
}

.chip-icon-compact {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 3px;
}

.chip-text-compact {
  font-weight: 600;
  margin-right: 3px;
}

.chip-label-compact {
  font-weight: 400;
  opacity: 0.8;
}

/* Aplicar los mismos colores a las versiones compactas */
.content-chip-compact.flashcard-chip {
  background: #e8f8f5;
  color: #2d7738;
  border: 1px solid rgba(45, 119, 56, 0.1);
}

.content-chip-compact.quiz-chip {
  background: #fff4e6;
  color: #e65100;
  border: 1px solid rgba(230, 81, 0, 0.1);
}

.content-chip-compact.explanation-chip {
  background: #e3f2fd;
  color: #1565c0;
  border: 1px solid rgba(21, 101, 192, 0.1);
}

.gap-2 {
  gap: 8px;
}

/* Badge "Más popular" - Ribbon style */
.popular-badge {
  margin: 0;
  padding: 0;
  color: white;
  border-radius: 0px !important;
  padding: 0.8em 0;
  position: absolute;
  top: 0;
  right: 0;
  transform: translateX(30%) translateY(0%) rotate(45deg);
  transform-origin: top left;
  font-family: 'Rubik', -apple-system, system-ui, sans-serif;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  box-shadow: 0 2px 8px rgba(77, 212, 83, 0.3);
  background: #4dd453;
  z-index: 3 !important;
}

.popular-badge:before,
.popular-badge:after {
  content: '';
  position: absolute;
  top: 0;
  margin: 0 -1px;
  width: 100%;
  height: 100%;
  background: #4dd453;
}

.popular-badge:before {
  right: 100%;
}

.popular-badge:after {
  left: 100%;
}

.popular-badge-text {
  display: block;
  text-align: center;
}

/* Badge "Más estudiado" - Ribbon style */
.studied-badge {
  margin: 0;
  padding: 0;
  padding: 0.8em 0;
  position: absolute;
  top: 0;
  right: 0;
  transform: translateX(30%) translateY(0%) rotate(45deg);
  transform-origin: top left;
  font-family: 'Rubik', -apple-system, system-ui, sans-serif;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  color: white;
  border-radius: 0px !important;
  background: #1565c0; /* Azul */
  box-shadow: 0 2px 8px rgba(21, 101, 192, 0.3);
  z-index: 3 !important;
}

.studied-badge:before,
.studied-badge:after {
  content: '';
  position: absolute;
  top: 0;
  margin: 0 -1px;
  width: 100%;
  height: 100%;
  background: #1565c0; /* Azul */
}

.studied-badge:before {
  right: 100%;
}

.studied-badge:after {
  left: 100%;
}

.studied-badge-text {
  display: block;
  text-align: center;
}
</style>